import { usePostHog } from '@posthog/react';
import { buttonStyle } from 'components/buttons/buttonStyle';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { SectionCard } from 'components/GridItemList';
import { PlusIcon } from 'components/icons';
import { EDIT_BUTTON_CLASS } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import { useInvoice } from 'pages/invoices/hooks/useInvoice';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useCreateInvoicePaymentMutation, useDeleteInvoicePaymentMutation } from 'store/api/invoices';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoicePaymentFormDto } from 'types/invoices';
import { useHasPermissions } from 'utils/permissionUtil';
import InvoicePaymentsList from './InvoicePaymentsList';

type Props = {
    classifiers: Record<string, ClassifierData>;
    invoice: InvoiceData;
    isInViewMode: boolean;
    handleStatusLoadingState: (isLoading: boolean) => void;
    /** The new design's card, titled by itself: Add payment in its header opens the payment's row, Cancel closes it. */
    isInCard?: boolean;
};

const RevenueTeamCard = ({ classifiers, invoice, isInViewMode, handleStatusLoadingState, isInCard }: Props) => {
    const posthog = usePostHog();
    const [isAdding, setIsAdding] = useState(false);
    const formMethods = useForm<InvoicePaymentFormDto>({
        values: {
            invoiceId: invoice.id,
            amount: '',
            paymentNote: '',
            date: null,
        },
        defaultValues: {
            invoiceId: invoice.id,
            amount: '',
            paymentNote: '',
            date: null,
        },
    });

    const [createInvoicePayment, { isLoading: isLoadingCreateInvoicePayment }] = useCreateInvoicePaymentMutation();

    const [deleteInvoicePayment, { isLoading: isLoadingDeleteInvoicePayment }] = useDeleteInvoicePaymentMutation();

    const isLoadingPayment = isLoadingCreateInvoicePayment || isLoadingDeleteInvoicePayment;

    const submitHandler = useCallback(
        async (data: InvoicePaymentFormDto) => {
            handleStatusLoadingState(true);
            await createInvoicePayment(data)
                .unwrap()
                .then(() => {
                    posthog.capture('invoice_payment_registered');
                    formMethods.reset();
                    setIsAdding(false);
                    handleStatusLoadingState(false);
                });
        },
        [createInvoicePayment, formMethods, handleStatusLoadingState, posthog]
    );

    const deleteHandler = useCallback(
        async (id: number) => {
            await deleteInvoicePayment({ invoiceId: invoice.id, paymentId: id }).unwrap().catch(console.error);
        },
        [deleteInvoicePayment, invoice.id]
    );

    const { isCredit, isPostponed, isCorrection, isPrepayment } = useInvoice(invoice);
    const isPaymentDisabled = isCredit || isCorrection || isPostponed || invoice.hasCreditInvoice;
    const isUserAuthorizedToCreatePayments = useHasPermissions(['CREATE.PAYMENTS.ALL']);
    const isInvoicePaymentFormDisabled = (isInViewMode && !isUserAuthorizedToCreatePayments) || isPaymentDisabled;
    const isAuthorizedToDeletePayments = useHasPermissions(['DELETE.PAYMENTS.ALL']);
    const isInvoicePaymentsReadonly = (isInViewMode && !isAuthorizedToDeletePayments) || isPaymentDisabled;
    const isPrepaymentLinkedToDebitInvoice = isPrepayment && !!invoice.prepaymentsUsed?.length;

    const paymentsList = (
        <InvoicePaymentsList
            invoice={invoice}
            classifiers={classifiers}
            deleteHandler={(id) => {
                void deleteHandler(id);
            }}
            isInViewMode={isInvoicePaymentsReadonly}
            isPrepaymentLinkedToDebitInvoice={isPrepaymentLinkedToDebitInvoice}
            isInCard={isInCard}
        />
    );
    const paymentRow = isInvoicePaymentFormDisabled ? null : (
        <div className="mt-4 flex flex-row flex-wrap justify-end gap-2.5">
            <DecimalInput
                control={formMethods.control}
                name="amount"
                placeholder="sum"
                required={true}
                disabled={isLoadingPayment}
                pattern="^-?[0-9]*[.,]?([0-9]{0,2})?$" // modified DecimalInput RegExp to allow negative numbers
                noFullWidthInputWrapper
            />
            <DateInput
                control={formMethods.control}
                name="date"
                wrapperClassName="max-w-[200px]"
                required={true}
                disabled={isLoadingPayment}
                noFullWidthInputWrapper
                fullWidth
            />
            <TextInput
                maxLength={100}
                name="paymentNote"
                wrapperClassName="min-w-[250px]"
                control={formMethods.control}
                placeholder="note"
                disabled={isLoadingPayment}
                noFullWidthInputWrapper
            />
            {isInCard ? (
                <Button
                    {...buttonStyle({ variant: 'outlined', size: 'small' }, 'h-9 self-end')}
                    onClick={() => {
                        formMethods.reset();
                        setIsAdding(false);
                    }}
                    disabled={isLoadingPayment}
                >
                    <FormattedMessage id="cancel" />
                </Button>
            ) : null}
            <Button
                {...buttonStyle({ variant: 'outlined', size: 'small' }, 'h-9 self-end')}
                onClick={() => {
                    void formMethods.handleSubmit(submitHandler)();
                }}
                disabled={isLoadingPayment}
            >
                <FormattedMessage id="invoices.general-tab.revenue-team.add-payment" />
            </Button>
        </div>
    );

    if (isInCard) {
        return (
            <SectionCard
                title={<FormattedMessage id="invoices.general-tab.revenue-team.title" />}
                action={
                    paymentRow && !isAdding ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className={EDIT_BUTTON_CLASS}
                            onClick={() => setIsAdding(true)}
                        >
                            <PlusIcon data-icon="inline-start" className="size-3.5" />
                            <FormattedMessage id="invoices.general-tab.revenue-team.add-payment" />
                        </Button>
                    ) : null
                }
            >
                <div className="flex flex-col overflow-hidden">
                    {paymentsList}
                    {isAdding ? paymentRow : null}
                </div>
            </SectionCard>
        );
    }

    return (
        <div className="flex flex-col overflow-hidden">
            {paymentsList}
            {paymentRow}
        </div>
    );
};

export default RevenueTeamCard;
